import { StandardEvents } from '@shopify/events';

class MetCullenPreview extends HTMLElement {
  connectedCallback() {
    this.registry = JSON.parse(this.querySelector('[data-cullen-registry]').textContent);
    this.posterCache = new Map();
    this.posterJobs = new Map();
    this.section = this.closest('.shopify-section');
    this.onClick = this.onClick.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.addEventListener('click', this.onClick);
    this.section?.addEventListener(StandardEvents.productSelect, this.onSelect);
    this.renderVariant(this.dataset.variantId);
  }

  disconnectedCallback() {
    this.sequence = (this.sequence || 0) + 1;
    this.clearMedia();
    this.posterCache?.forEach((url) => URL.revokeObjectURL(url));
    this.posterCache?.clear();
    this.removeEventListener('click', this.onClick);
    this.section?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  imageUrl(url, width) {
    const value = new URL(url);
    value.searchParams.set('width', width);
    return value.href;
  }

  releaseVideo(video) {
    if (!video) return;
    video.pause();
    video.onloadedmetadata = video.onloadeddata = video.onseeked = video.onerror = null;
    video.removeAttribute('src');
    video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
    video.load();
  }

  clearMedia() {
    this.renderSequence = (this.renderSequence || 0) + 1;
    this.thumbObserver?.disconnect();
    this.posterJobs?.forEach((job) => job.cancel());
    this.releaseVideo(this.querySelector('[data-cullen-stage] video'));
  }

  posterFor(url) {
    if (this.posterCache.has(url)) return Promise.resolve(this.posterCache.get(url));
    if (this.posterJobs.has(url)) return this.posterJobs.get(url).promise;
    const video = document.createElement('video');
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    let finished = false;
    let capturing = false;
    let timer;
    const finish = (poster) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      this.releaseVideo(video);
      this.posterJobs.delete(url);
      resolve(poster);
    };
    const job = { promise, cancel: () => finish(null) };
    this.posterJobs.set(url, job);
    const capture = () => {
      if (finished || capturing || video.seeking || video.readyState < 2 || !video.videoWidth) return;
      capturing = true;
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 1600 / Math.max(video.videoWidth, video.videoHeight));
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (finished) return;
          const poster = blob ? URL.createObjectURL(blob) : null;
          if (poster) this.posterCache.set(url, poster);
          finish(poster);
        }, 'image/jpeg', 0.9);
      } catch {
        finish(null);
      }
    };
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      try { video.currentTime = Math.min(0.001, video.duration / 2 || 0.001); }
      catch { capture(); }
    };
    video.onloadeddata = capture;
    video.onseeked = capture;
    video.onerror = () => finish(null);
    timer = setTimeout(() => finish(null), 15000);
    video.src = url;
    video.load();
    return promise;
  }

  async renderVideoThumb(button, url, sequence) {
    const poster = await this.posterFor(url);
    if (!poster || !this.isConnected || sequence !== this.renderSequence || !button.isConnected) return;
    const img = document.createElement('img');
    img.src = poster; img.alt = ''; img.width = 72; img.height = 90;
    button.firstElementChild.replaceWith(img);
    const video = this.querySelector('[data-cullen-stage] video');
    if (video && this.items?.[this.index]?.url === url) video.poster = poster;
  }

  renderVariant(id) {
    const binding = this.registry.variants[String(id)];
    this.clearMedia();
    this.dataset.variantId = String(id);
    const stage = this.querySelector('[data-cullen-stage]');
    const thumbs = this.querySelector('[data-cullen-thumbs]');
    stage.replaceChildren(); thumbs.replaceChildren();
    const status = this.querySelector('[data-cullen-status]');
    if (!binding || !this.registry.groups[binding.group]) {
      status.textContent = this.dataset.unavailable;
      this.items = [];
      return;
    }
    this.binding = binding;
    this.items = this.registry.groups[binding.group];
    status.textContent = binding.selectedCarat !== binding.depictedCarat
      ? `${this.dataset.depicted} ${binding.depictedCarat} ct · ${this.dataset.selected} ${binding.selectedCarat} ct`
      : `${this.dataset.depicted} ${binding.depictedCarat} ct`;
    this.items.forEach((item, index) => {
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.cullenIndex = index;
      button.setAttribute('aria-label', `${this.dataset.mediaLabel} ${index + 1}`);
      button.setAttribute('aria-pressed', 'false');
      if (item.type === 'video') {
        button.dataset.cullenVideoUrl = item.url;
        const canvas = document.createElement('canvas');
        canvas.width = 72; canvas.height = 90; canvas.setAttribute('aria-hidden', 'true');
        button.append(canvas);
        const play = document.createElement('span'); play.textContent = '▶';
        play.setAttribute('aria-hidden', 'true'); button.append(play);
      } else {
        const img = document.createElement('img');
        img.src = this.imageUrl(item.url, 144);
        img.alt = ''; img.width = 72; img.height = 90; img.loading = 'lazy';
        button.append(img);
      }
      thumbs.append(button);
    });
    const sequence = this.renderSequence;
    if ('IntersectionObserver' in window) {
      this.thumbObserver = new IntersectionObserver((entries) => {
        if (sequence !== this.renderSequence) return;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.thumbObserver.unobserve(entry.target);
          this.renderVideoThumb(entry.target, entry.target.dataset.cullenVideoUrl, sequence);
        });
      }, { root: thumbs, rootMargin: '80px' });
    }
    thumbs.querySelectorAll('[data-cullen-video-url]').forEach((button) => {
      const url = button.dataset.cullenVideoUrl;
      if (this.posterCache.has(url) || !this.thumbObserver) this.renderVideoThumb(button, url, sequence);
      else this.thumbObserver.observe(button);
    });
    this.show(0);
  }

  show(index) {
    const item = this.items?.[index]; if (!item) return;
    this.releaseVideo(this.querySelector('[data-cullen-stage] video')); this.index = index;
    this.querySelectorAll('[data-cullen-index]').forEach((b, i) => b.setAttribute('aria-pressed', String(i === index)));
    const stage = this.querySelector('[data-cullen-stage]'); stage.replaceChildren();
    if (item.type === 'video') {
      const video = document.createElement('video');
      const source = document.createElement('source'); source.src = item.url; source.type = 'video/mp4';
      video.append(source); video.controls = true; video.playsInline = true;
      video.preload = 'metadata'; video.setAttribute('aria-label', (this.dataset.displayTitle || this.registry.title));
      const poster = this.posterCache.get(item.url);
      if (poster) video.poster = poster;
      video.onloadedmetadata = () => {
        if (!video.paused || video.currentTime) return;
        try { video.currentTime = Math.min(0.001, video.duration / 2 || 0.001); } catch {}
      };
      stage.append(video);
      const button = this.querySelector(`[data-cullen-index="${index}"]`);
      this.thumbObserver?.unobserve(button);
      this.renderVideoThumb(button, item.url, this.renderSequence);
    } else {
      const button = document.createElement('button'); button.type = 'button'; button.dataset.cullenZoom = '';
      button.setAttribute('aria-label', this.dataset.zoomLabel);
      const img = document.createElement('img'); img.src = this.imageUrl(item.url, 1200);
      img.width = 1600; img.height = 2000; img.alt = `${(this.dataset.displayTitle || this.registry.title)} · ${this.binding.depictedCarat} ct`;
      img.loading = 'eager'; button.append(img); stage.append(button);
    }
  }

  onClick(event) {
    const button = event.target.closest('button'); if (!button || !this.contains(button)) return;
    if (button.hasAttribute('data-cullen-index')) this.show(Number(button.dataset.cullenIndex));
    if (button.hasAttribute('data-cullen-zoom')) {
      const dialog = this.querySelector('dialog');
      dialog.querySelector('img').src = this.imageUrl(this.items[this.index].url, 2000);
      dialog.showModal();
    }
    if (button.hasAttribute('data-cullen-close')) this.querySelector('dialog').close();
  }

  onSelect(event) {
    const picker = event.target instanceof Element && event.target.closest('variant-picker');
    if (!picker || picker.dataset.productId !== this.dataset.productId) return;
    const owner = this.closest('product-component');
    if (owner && event.target.closest('product-component') !== owner) return;
    const sequence = (this.sequence || 0) + 1; this.sequence = sequence;
    this.clearMedia(); this.setAttribute('aria-busy', 'true');
    event.promise.then(({ detail }) => {
      if (!this.isConnected || sequence !== this.sequence) return;
      if (String(detail?.productId) !== this.dataset.productId || !detail?.resource?.id) this.renderVariant('');
      else this.renderVariant(String(detail.resource.id));
      this.removeAttribute('aria-busy');
    }).catch(() => { if (this.isConnected && sequence === this.sequence) { this.renderVariant(''); this.removeAttribute('aria-busy'); } });
  }
}

if (!customElements.get('met-cullen-preview')) customElements.define('met-cullen-preview', MetCullenPreview);
